const pad = (n: number) => n.toString().padStart(2, '0')

export const toLocalISO = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`

export function getScheduleTimes(
  mode: 'now' | 'single' | 'spread' | 'daily',
  count: number,
  opts: {
    scheduleAt: string
    spreadStartTime: string
    spreadEndTime: string
    dailyStartDate: string
    dailyTime: string
  },
): string[] {
  if (mode === 'now') return Array(count).fill('')
  if (mode === 'single') return Array(count).fill(opts.scheduleAt)

  if (mode === 'spread') {
    if (!opts.scheduleAt) return Array(count).fill('')
    const baseDate = opts.scheduleAt.split('T')[0]
    const start = new Date(`${baseDate}T${opts.spreadStartTime}`)
    const end = new Date(`${baseDate}T${opts.spreadEndTime}`)
    const totalMs = end.getTime() - start.getTime()
    if (count <= 1 || totalMs <= 0) return [toLocalISO(start)]
    const gap = totalMs / (count - 1)
    return Array.from({ length: count }, (_, i) => toLocalISO(new Date(start.getTime() + i * gap)))
  }

  if (mode === 'daily') {
    if (!opts.dailyStartDate) return Array(count).fill('')
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(`${opts.dailyStartDate}T${opts.dailyTime}`)
      d.setDate(d.getDate() + i)
      return toLocalISO(d)
    })
  }

  return Array(count).fill('')
}

export function getBatchScheduleTime(
  index: number,
  mode: 'now' | 'single' | 'spread' | 'daily',
  count: number,
  batchInterval: number,
  scheduleAt: string,
  times: string[],
): string {
  if (mode === 'single' && scheduleAt && count > 1) {
    const base = new Date(scheduleAt)
    return toLocalISO(new Date(base.getTime() + index * batchInterval * 60000))
  }
  return times[index] || ''
}

/**
 * Extract source name from clip filename.
 * Pattern: "sourceName - PARTE N.ext" → "sourceName"
 * Falls back to full filename (minus extension) if no match.
 */
export function extractClipSource(filename: string): string {
  const match = filename.match(/^(.+?)\s*-\s*PARTE\s+\d+/i)
  if (match) return match[1].trim()
  return filename.replace(/\.[^.]+$/, '')
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/**
 * Build a day-by-day schedule where each source video's clips go to one page
 * per day (sequential, 1-min interval), and the same video is never on two
 * pages on the same day.
 *
 * Returns a flat list of publish operations:
 *   { item, destIndex, dayOffset, clipIndex }
 *
 * Example: 3 sources (A,B,C), 3 destinations, startTime 10:00
 *   Day 0: Dest0=A(parts 1-6 at 10:00-10:05), Dest1=B, Dest2=C
 *   Day 1: Dest0=B, Dest1=C, Dest2=A  (rotated+shuffled)
 *   Day 2: Dest0=C, Dest1=A, Dest2=B
 *
 * Each page gets ALL sources over multiple days. Same source never on 2 pages same day.
 */
export interface ClipScheduleOp<T> {
  item: T
  destIndex: number
  dayOffset: number
  clipIndex: number // 0-based within the group for that day
}

export function buildDayRotatedClipSchedule<T extends { id: string; filename: string }>(
  items: T[],
  destinationCount: number,
): ClipScheduleOp<T>[] {
  // Group by source
  const groups = new Map<string, T[]>()
  for (const item of items) {
    const source = extractClipSource(item.filename)
    if (!groups.has(source)) groups.set(source, [])
    groups.get(source)!.push(item)
  }

  const sourceGroups = shuffle(Array.from(groups.values()))
  const numSources = sourceGroups.length
  const ops: ClipScheduleOp<T>[] = []

  // Each source needs to visit each destination on a different day.
  // Day d, destination p gets source (d + p) % numSources
  // Total days = numSources (each source visits each dest once if dests <= sources)
  // If dests > sources, we wrap and add extra rounds.
  const totalDays = numSources

  for (let day = 0; day < totalDays; day++) {
    for (let dest = 0; dest < destinationCount; dest++) {
      const sourceIdx = (day + dest) % numSources
      const group = sourceGroups[sourceIdx]
      for (let c = 0; c < group.length; c++) {
        ops.push({
          item: group[c],
          destIndex: dest,
          dayOffset: day,
          clipIndex: c,
        })
      }
    }
  }

  return ops
}
